import type { RegimeDependenceProfileReport, RegimeProfileDimensionResult, RegimeProfileThresholds } from '@/types/appRouter';
import { Callout, Card, Disclosure, Tooltip } from '@/components/UI';
import {
    buildInteractionMatrix,
    formatInt,
    formatShare,
    formatSignedPercent,
    groupDimensionsByRole,
    REGIME_DIMENSION_LABELS
} from './regimeProfilePresentation';
import { RegimeSliceTable } from './RegimeSliceTable';

function dimensionLabel(dimension: RegimeProfileDimensionResult): string {
    return REGIME_DIMENSION_LABELS[dimension.key];
}

/**
 * Full-width degraded-inputs banner (design rule 4 — a banner, not a footnote). The v1 cause is pinned
 * by the backend plan §2.1: non-SPY/QQQ entity SMA statistics frozen at 2021-12-31.
 */
function DegradedInputsCallout({ dimensionLabels }: { dimensionLabels: string[] }) {
    return (
        <Callout className="w-full" title="Degraded inputs" variant="warning">
            Non-SPY/QQQ entity SMA statistics are frozen at 2021-12-31, contaminating {dimensionLabels.join(' and ')} classification.
            Directional context only — this panel never produces actionables.
        </Callout>
    );
}

function InteractionMatrix({ dimension, thresholds }: { dimension: RegimeProfileDimensionResult; thresholds: RegimeProfileThresholds }) {
    const matrix = buildInteractionMatrix(dimension);

    return (
        <div className="overflow-x-auto">
            <table className="w-full text-xs">
                <thead>
                    <tr>
                        <th className="py-1.5 pr-3 text-left font-medium text-zinc-500">Market composite ↓</th>
                        {matrix.columnStates.map((columnState) => (
                            <th className="px-2 py-1.5 text-right font-medium text-zinc-400" key={columnState}>
                                {columnState}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                    {matrix.rowStates.map((rowState) => (
                        <tr key={rowState}>
                            <td className="py-1.5 pr-3 text-zinc-300">{rowState}</td>
                            {matrix.columnStates.map((columnState) => {
                                const slice = matrix.cellsByKey.get(`${rowState}|${columnState}`);

                                if (!slice) {
                                    return (
                                        <td className="px-2 py-1.5 text-right text-zinc-600" key={columnState}>
                                            —
                                        </td>
                                    );
                                }

                                const belowMinimum = slice.tradeCount < thresholds.minTradesPerSlice;
                                const valueClass = belowMinimum
                                    ? 'text-zinc-500'
                                    : slice.meanProfitPercent < 0
                                      ? 'text-red-400'
                                      : 'text-green-400';

                                return (
                                    <td className="px-2 py-1.5 text-right" key={columnState}>
                                        <Tooltip
                                            content={[
                                                `${rowState} | ${columnState}`,
                                                `Mean profit (winsorized): ${formatSignedPercent(slice.meanProfitPercent)}`,
                                                `${formatInt(slice.tradeCount)} trades · ${formatInt(slice.episodeCount)} episodes`,
                                                `Win rate ${formatShare(slice.winRate, 1)}`,
                                                ...(belowMinimum
                                                    ? [`Below the ${thresholds.minTradesPerSlice}-trade evaluation minimum`]
                                                    : [])
                                            ].join('\n')}
                                        >
                                            <span className={`cursor-help tabular-nums ${valueClass}`}>
                                                {formatSignedPercent(slice.meanProfitPercent, 1)}
                                                <span className="ml-1 text-[10px] text-zinc-500">{formatInt(slice.episodeCount)}ep</span>
                                            </span>
                                        </Tooltip>
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
            {matrix.unparseableStates.length > 0 && (
                <p className="mt-2 text-xs text-amber-400">Unrecognized interaction state keys: {matrix.unparseableStates.join(', ')}</p>
            )}
        </div>
    );
}

/** Badge kept on the collapsed header so the degradation stays visible while the panel is closed. */
function DegradedInputsBadge() {
    return (
        <span className="rounded border border-amber-700 bg-amber-900/40 px-1.5 py-0.5 text-[10px] font-medium text-amber-300">
            degraded inputs
        </span>
    );
}

function CollapsedDimensionDisclosure({
    dimension,
    thresholds
}: {
    dimension: RegimeProfileDimensionResult;
    thresholds: RegimeProfileThresholds;
}) {
    return (
        <Disclosure badge={dimension.degradedInputs ? <DegradedInputsBadge /> : undefined} title={dimensionLabel(dimension)}>
            <div className="space-y-3">
                {dimension.degradedInputs && <DegradedInputsCallout dimensionLabels={[dimensionLabel(dimension)]} />}
                <RegimeSliceTable dimension={dimension} thresholds={thresholds} />
            </div>
        </Disclosure>
    );
}

interface RegimeSupportingDimensionsProps {
    report: Pick<RegimeDependenceProfileReport, 'dimensions' | 'thresholds'>;
}

/**
 * Layer 4 — supporting dimensions, deliberately subordinate (design rule 3). The drill-downs collapse
 * and indent under the primary panel because they explain the composite rather than confirm it; only
 * the O'Neil framework renders at primary weight as a genuine second opinion.
 */
export function RegimeSupportingDimensions({ report }: RegimeSupportingDimensionsProps) {
    const groups = groupDimensionsByRole(report.dimensions);
    const degradedContextualLabels = groups.contextual.filter((dimension) => dimension.degradedInputs).map(dimensionLabel);
    const degradedInteractionLabels = groups.interactions.filter((dimension) => dimension.degradedInputs).map(dimensionLabel);

    return (
        <div className="space-y-6">
            {groups.drillDown.length > 0 && (
                <div className="ml-4 border-l-2 border-zinc-800 pl-4">
                    <p className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">
                        Explains the composite above. Not independent evidence.
                    </p>
                    <div className="space-y-2">
                        {groups.drillDown.map((dimension) => (
                            <CollapsedDimensionDisclosure dimension={dimension} key={dimension.key} thresholds={report.thresholds} />
                        ))}
                    </div>
                </div>
            )}

            {groups.independent.map((dimension) => (
                <Card className="bg-zinc-900 border border-zinc-800" key={dimension.key}>
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                        <h3 className="text-sm font-medium text-zinc-100">{dimensionLabel(dimension)}</h3>
                        <span className="rounded border border-blue-800 bg-blue-950/40 px-1.5 py-0.5 text-[10px] font-medium text-blue-300">
                            Independent framework
                        </span>
                    </div>
                    {dimension.degradedInputs && (
                        <div className="mb-3">
                            <DegradedInputsCallout dimensionLabels={[dimensionLabel(dimension)]} />
                        </div>
                    )}
                    <RegimeSliceTable dimension={dimension} thresholds={report.thresholds} />
                </Card>
            ))}

            {groups.contextual.length > 0 && (
                <div>
                    <p className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">Contextual dimensions</p>
                    {degradedContextualLabels.length > 0 && (
                        <div className="mb-3">
                            <DegradedInputsCallout dimensionLabels={degradedContextualLabels} />
                        </div>
                    )}
                    <div className="space-y-2">
                        {groups.contextual.map((dimension) => (
                            <CollapsedDimensionDisclosure dimension={dimension} key={dimension.key} thresholds={report.thresholds} />
                        ))}
                    </div>
                </div>
            )}

            {groups.interactions.length > 0 && (
                <div>
                    <p className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">Predeclared interactions</p>
                    {degradedInteractionLabels.length > 0 && (
                        <div className="mb-3">
                            <DegradedInputsCallout dimensionLabels={degradedInteractionLabels} />
                        </div>
                    )}
                    <div className="space-y-2">
                        {groups.interactions.map((dimension) => (
                            <Disclosure
                                badge={dimension.degradedInputs ? <DegradedInputsBadge /> : undefined}
                                key={dimension.key}
                                title={dimensionLabel(dimension)}
                            >
                                <div className="space-y-3">
                                    {dimension.degradedInputs && <DegradedInputsCallout dimensionLabels={[dimensionLabel(dimension)]} />}
                                    <InteractionMatrix dimension={dimension} thresholds={report.thresholds} />
                                </div>
                            </Disclosure>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
